import type { OffsetPageQueryDTO } from "@/shared/contracts/base";
import { config } from "@/shared/lib/config";
import { fetcher } from "@/shared/lib/fetcher";
import type {
  CreateKPIAssignmentInput,
  CreateKPIInput,
  UpdateKPIAssignmentInput,
  UpdateKPIInput,
} from "../../domain/contracts";
import type {
  KPI,
  KPIAssignmentImpact,
  KPIAssignmentListItem,
  KPIListItem,
  KPIOverviewExplainResult,
  KPIOverviewSummary,
  KPIPeriodFilter,
} from "../../domain/types";

const baseUrl = `${config.apiUrl}/kpi`;

export type KPIDTO = KPI;
export type KPIsListResult = {
  data: KPIListItem[];
  meta: {
    total: number;
    limit: number;
    offset: number;
  };
};

export const kpiApi = {
  async getOverview(filter: KPIPeriodFilter): Promise<KPIOverviewSummary> {
    const searchParams = new URLSearchParams();
    searchParams.set("periodType", filter.periodType);
    searchParams.set("year", String(filter.year));

    if (filter.periodType === "monthly" && filter.month) {
      searchParams.set("month", String(filter.month));
    } else if (filter.periodType === "quarterly" && filter.quarter) {
      searchParams.set("quarter", String(filter.quarter));
    }
    if (filter.departmentId)
      searchParams.set("departmentId", filter.departmentId);
    if (filter.search) searchParams.set("search", filter.search);

    const url = `${baseUrl}/overview?${searchParams.toString()}`;
    return fetcher.get<KPIOverviewSummary>(url);
  },

  async getOverviewExplain(
    filter: KPIPeriodFilter,
    args: {
      scope: "organization" | "department" | "position" | "employee";
      scopeDepartmentId?: string;
      scopePositionId?: string;
      kpiId?: string;
      /** Phase B: ຂໍສະຫຼຸບຕໍ່ແຫຼ່ງຂໍ້ມູນ (ຫຼື ໃຊ້ kpiId ໃນ API ເພື່ອເປີດອັດຕະໂນມັດ) */
      includeSources?: boolean;
      /** Phase C: ຕົວຢ່າງບັນທຶກພ້ອມລິ້ງ */
      includeRecordSamples?: boolean;
      recordSampleLimit?: number;
    },
  ): Promise<KPIOverviewExplainResult> {
    const searchParams = new URLSearchParams();
    searchParams.set("periodType", filter.periodType);
    searchParams.set("year", String(filter.year));
    if (filter.periodType === "monthly" && filter.month) {
      searchParams.set("month", String(filter.month));
    } else if (filter.periodType === "quarterly" && filter.quarter) {
      searchParams.set("quarter", String(filter.quarter));
    }
    if (filter.departmentId) {
      searchParams.set("departmentId", filter.departmentId);
    }
    if (filter.search) searchParams.set("search", filter.search);
    searchParams.set("scope", args.scope);
    if (args.scopeDepartmentId) {
      searchParams.set("scopeDepartmentId", args.scopeDepartmentId);
    }
    if (args.scopePositionId) {
      searchParams.set("scopePositionId", args.scopePositionId);
    }
    if (args.kpiId) searchParams.set("kpiId", args.kpiId);
    if (args.includeSources) searchParams.set("includeSources", "1");
    if (args.includeRecordSamples) searchParams.set("includeRecordSamples", "1");
    if (args.recordSampleLimit != null) {
      searchParams.set("recordSampleLimit", String(args.recordSampleLimit));
    }

    const url = `${baseUrl}/overview/explain?${searchParams.toString()}`;
    return fetcher.get<KPIOverviewExplainResult>(url);
  },

  async list(query: OffsetPageQueryDTO): Promise<KPIsListResult> {
    const params = new URLSearchParams();
    params.set("limit", String(query.limit ?? 20));
    params.set("offset", String(query.offset ?? 0));
    if (query.sort) params.set("sort", JSON.stringify(query.sort));
    if (query.filters) params.set("filters", JSON.stringify(query.filters));

    const url = `${baseUrl}?${params.toString()}`;
    return fetcher.get<KPIsListResult>(url);
  },

  async get(id: string): Promise<KPIDTO> {
    return fetcher.get<KPIDTO>(`${baseUrl}/${id}`);
  },

  async create(input: CreateKPIInput): Promise<KPIDTO> {
    return fetcher.post<KPIDTO>(baseUrl, input);
  },

  async update(id: string, input: UpdateKPIInput): Promise<KPIDTO> {
    return fetcher.patch<KPIDTO>(`${baseUrl}/${id}`, input);
  },

  async delete(id: string): Promise<{ success: boolean }> {
    return fetcher.delete<{ success: boolean }>(`${baseUrl}/${id}`);
  },

  async copy(id: string): Promise<KPIDTO> {
    return fetcher.post<KPIDTO>(`${baseUrl}/${id}/copy`, {});
  },

  async activate(id: string): Promise<KPIDTO> {
    return fetcher.post<KPIDTO>(`${baseUrl}/${id}/activate`, {});
  },

  async deactivate(id: string): Promise<KPIDTO> {
    return fetcher.post<KPIDTO>(`${baseUrl}/${id}/deactivate`, {});
  },
};

// KPI calculation by period (preview + recalculate)
export interface KpiCalculationPeriodParams {
  year: number;
  month?: number;
  quarter?: number;
}

export interface PreviewKpiItem {
  id: string;
  name: string;
  level: "employee" | "position" | "department" | "organization";
}

export interface PreviewSourceItem {
  source: string;
  connected: boolean;
  recordCount: number;
  kpis: PreviewKpiItem[];
  message?: string;
}

export interface PreviewForPeriodResult {
  sources: PreviewSourceItem[];
}

export interface RecalcByPeriodResult {
  totalUpdated: number;
  checklist: number;
  qc: number;
  internal_audit: number;
  quotation_invoice: number;
}

export const kpiCalculationApi = {
  async getPreview(
    params: KpiCalculationPeriodParams,
  ): Promise<PreviewForPeriodResult> {
    const search = new URLSearchParams();
    search.set("year", String(params.year));
    if (params.month != null) search.set("month", String(params.month));
    if (params.quarter != null) search.set("quarter", String(params.quarter));
    return fetcher.get<PreviewForPeriodResult>(
      `${baseUrl}/calculation/preview?${search.toString()}`,
    );
  },

  async recalculate(
    params: KpiCalculationPeriodParams,
  ): Promise<RecalcByPeriodResult> {
    return fetcher.post<RecalcByPeriodResult>(
      `${baseUrl}/calculation/recalculate`,
      params,
    );
  },
};

export type KPIAssignmentsListResult = {
  data: KPIAssignmentListItem[];
  meta: {
    total: number;
    limit: number;
    offset: number;
  };
};

export const kpiAssignmentApi = {
  async list(
    query: OffsetPageQueryDTO & {
      kpiId?: string;
      assignmentType?: "employee" | "position" | "department";
      isActive?: boolean;
      departmentId?: string;
    },
  ): Promise<KPIAssignmentsListResult> {
    const params = new URLSearchParams();
    params.set("limit", String(query.limit ?? 20));
    params.set("offset", String(query.offset ?? 0));
    if (query.sort) params.set("sort", JSON.stringify(query.sort));
    if (query.filters) params.set("filters", JSON.stringify(query.filters));
    if (query.kpiId) params.set("kpiId", query.kpiId);
    if (query.assignmentType)
      params.set("assignmentType", query.assignmentType);
    if (query.isActive !== undefined)
      params.set("isActive", String(query.isActive));
    if (query.departmentId) params.set("departmentId", query.departmentId);

    const url = `${baseUrl}/assignments?${params.toString()}`;
    return fetcher.get<KPIAssignmentsListResult>(url);
  },

  async get(id: string): Promise<KPIAssignmentListItem> {
    return fetcher.get<KPIAssignmentListItem>(`${baseUrl}/assignments/${id}`);
  },

  async create(
    input: CreateKPIAssignmentInput,
  ): Promise<KPIAssignmentListItem> {
    return fetcher.post<KPIAssignmentListItem>(`${baseUrl}/assignments`, input);
  },

  async update(
    id: string,
    input: UpdateKPIAssignmentInput,
  ): Promise<KPIAssignmentListItem> {
    return fetcher.patch<KPIAssignmentListItem>(
      `${baseUrl}/assignments/${id}`,
      input,
    );
  },

  async delete(id: string): Promise<{ success: boolean; hasResults: boolean }> {
    return fetcher.delete<{ success: boolean; hasResults: boolean }>(
      `${baseUrl}/assignments/${id}`,
    );
  },

  async getImpact(kpiId: string): Promise<KPIAssignmentImpact> {
    return fetcher.get<KPIAssignmentImpact>(`${baseUrl}/${kpiId}/impact`);
  },
};
